import React, { useState } from 'react';
import { Box, Button, CircularProgress, Divider, Grid, IconButton, Modal, Paper, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import BulkUploadTemplateSelector from './BulkUploadTemplateSelector';
import FileUpload from '../../../components/FileUpload';
import { apiClient } from '../../../utils/axios';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useNavigate } from 'react-router-dom';

const BulkUploadButtonAction = ({ open, onCancel, document_type }) => {
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const onFileUpload = (file) => {
    setFile(file);
  };

  const onDownloadTemplate = () => {
    setDownloadingTemplate(true);
    apiClient
      .get('/bulk-uploads/template/download', {
        responseType: 'blob',
        params: { template_name: selectedTemplate },
      })
      .then((response) => {
        dispatch(showSnackbar({ message: 'Template downloaded successfully!', severity: 'success' }));
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${selectedTemplate}.xlsx`);
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        dispatch(
          showSnackbar({
            message: 'Error downloading the template. Please try again later',
            severity: 'error',
          }),
        );
      })
      .finally(() => {
        setDownloadingTemplate(false);
      });
  };

  const onSubmit = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', document_type);
    formData.append('template_name', selectedTemplate);

    apiClient
      .post('/bulk-uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        if (response.status > 200 && response.status < 300) {
          dispatch(showSnackbar({ message: 'File upload successfully!', severity: 'success' }));
          onCancel();
          navigate('/bulk-upload');
        }
      })
      .catch((error) => {
        dispatch(
          showSnackbar({
            message: 'Error uploading the selected file. Please try again later',
            severity: 'error',
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal open={open} onClose={onCancel}>
      <Paper
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          padding: 16,
          boxShadow: 24,
          outline: 'none',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            Bulk Upload E-Documents
          </Typography>
          <IconButton onClick={onCancel}>
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <Grid sx={{ pt: 1 }}>
          <Typography variant="subtitle2" sx={{ py: 1 }}>
            1. Select a template
          </Typography>
          <BulkUploadTemplateSelector onSelect={onTemplateSelect} />
        </Grid>
        <Grid sx={{ pt: 1 }}>
          <Typography variant="subtitle2" sx={{ py: 1 }}>
            2. Upload your file
          </Typography>
          <FileUpload onUpload={onFileUpload} supportedFormats={['.xlsx', '.csv']} />
          <Grid alignItems="center" justifyContent="center" sx={{ pt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Need the template? Download here:
            </Typography>
            <Button
              variant="text"
              color="primary"
              disabled={!selectedTemplate || downloadingTemplate}
              onClick={onDownloadTemplate}
              startIcon={downloadingTemplate ? <CircularProgress size={20} /> : null}
            >
              {downloadingTemplate ? 'Downloading...' : 'Download Template'}
            </Button>
          </Grid>
        </Grid>
        <Divider />
        <Grid mt={2} textAlign="right">
          <Button
            id="upload-start-processing"
            variant="contained"
            color="primary"
            disabled={!selectedTemplate || !file || loading || downloadingTemplate}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            onClick={onSubmit}
          >
            {loading ? 'Processing...' : 'Upload & Start Processing'}
          </Button>
        </Grid>
      </Paper>
    </Modal>
  );
};

export default BulkUploadButtonAction;
